import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Mail, Plus, UserMinus, UsersRound, X } from "lucide-react"
import { api } from "../../api/client"
import { useApp } from "../../state/AppState"
import { Avatar, Button, Card, EmptyState, Modal, cx } from "../ui"
import { plural } from "../../lib/format"

/**
 * The teams on a project, and the people on them.
 *
 * A team is attached to the project, not to an issue. Everyone on it sees the
 * whole project - every question already raised and every one raised later -
 * which is the point: the people doing a job need the job, not a stream of
 * invitations to each question that comes up on it.
 *
 * This panel is where the project owner does that once, and where anyone on
 * the project can see who else is on it.
 */
export function ProjectTeams({ project, teams, onChanged }) {
	const { user } = useApp()
	const [adding, setAdding] = useState(false)
	const [removing, setRemoving] = useState(null)

	const isOwner = Boolean(project?.is_owner)
	const headcount = new Set(
		teams.flatMap((team) => team.members.map((member) => member.user_id))
	).size

	return (
		<section className="space-y-3">
			<div className="flex flex-wrap items-end justify-between gap-3">
				<div>
					<h2 className="flex items-center gap-2 text-[0.9375rem] font-semibold text-ink-900">
						<UsersRound size={16} className="text-ink-400" aria-hidden />
						On this project
					</h2>
					<p className="mt-0.5 text-[0.8125rem] leading-relaxed text-ink-500">
						{teams.length === 0
							? "No team on this project yet."
							: `${plural(headcount, "person", "people")} across ${plural(
									teams.length,
									"team"
								)} — each of them sees every issue here.`}
					</p>
				</div>
				{isOwner && (
					<Button icon={Plus} onClick={() => setAdding(true)}>
						Invite a team
					</Button>
				)}
			</div>

			{teams.length === 0 ? (
				<EmptyState
					icon={UsersRound}
					title="No team on this project"
					description={
						isOwner
							? "Add a team and everyone on it sees every question here, including the ones raised later. To give one person one question, use Invite on that question."
							: "The project owner has not put a team on this project."
					}
					action={
						isOwner ? (
							<Button variant="primary" icon={Plus} onClick={() => setAdding(true)}>
								Invite a team
							</Button>
						) : null
					}
				/>
			) : (
				<div className="space-y-3">
					{teams.map((team) => (
						<TeamPanel
							key={team.team_id}
							team={team}
							currentUserId={user?.user_id}
							canRemove={isOwner}
							onRemove={() => setRemoving(team)}
						/>
					))}
				</div>
			)}

			<AddTeamDialog
				open={adding}
				onClose={() => setAdding(false)}
				projectId={project?.project_id}
				attached={teams}
				onAdded={async () => {
					await onChanged()
					setAdding(false)
				}}
			/>

			<RemoveTeamDialog
				team={removing}
				projectId={project?.project_id}
				onClose={() => setRemoving(null)}
				onRemoved={onChanged}
			/>
		</section>
	)
}

/* -------------------------------------------------------------------------- */

/** One team, with every person spelled out rather than reduced to an avatar. */
function TeamPanel({ team, currentUserId, canRemove, onRemove }) {
	return (
		<Card className="overflow-hidden">
			<div className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-100 bg-ink-50/70 px-4 py-2.5">
				<div className="flex items-center gap-2">
					<span className="grid h-6 w-6 place-items-center rounded-md bg-brand-100 text-brand-800">
						<UsersRound size={13} aria-hidden />
					</span>
					<h3 className="text-[0.875rem] font-semibold text-ink-900">{team.name}</h3>
					<span className="text-2xs text-ink-500">
						{plural(team.size, "person", "people")}
					</span>
				</div>
				{canRemove && (
					<Button size="sm" variant="ghost" icon={UserMinus} onClick={onRemove}>
						Remove from project
					</Button>
				)}
			</div>

			<PersonGrid
				members={team.members}
				pendingEmails={team.pending_emails}
				currentUserId={currentUserId}
			/>
		</Card>
	)
}

/**
 * Name, then job title and company on one line, then the address.
 *
 * Those are the things that tell you whose answer you are reading; an avatar
 * and a first name does not, when half the thread works for someone else. The
 * address goes on its own line because it is the one field you might need to
 * copy, and the one that identifies two people with the same name apart.
 */
export function PersonGrid({
	members,
	pendingEmails = [],
	currentUserId,
	className,
	// Supplied on the teams page, where the group is edited. Omitted on a
	// project page, where the list is a statement of who is on the job rather
	// than a control.
	onRemoveMember,
	onRemovePending,
	lockedId
}) {
	return (
		// Separate cards on a tinted tray, rather than flush cells divided by a
		// hairline: a one-person team in a two-column grid left an empty grey
		// half looking like a broken second cell.
		<div
			className={cx(
				"grid gap-3 bg-ink-50/70 p-4 sm:grid-cols-2",
				className
			)}
		>
			{members.map((member) => (
				<div
					key={member.user_id}
					className="group flex items-center gap-3 rounded-xl border border-ink-200/80 bg-white px-3.5 py-3 shadow-card"
				>
					<Avatar person={member} size="md" />
					<div className="min-w-0 flex-1">
						<p className="truncate text-[0.875rem] font-medium leading-tight text-ink-900">
							{member.name}
							{member.user_id === currentUserId && (
								<span className="ml-1.5 rounded-full bg-brand-50 px-1.5 py-0.5 text-2xs font-semibold text-brand-800">
									You
								</span>
							)}
						</p>
						<p className="mt-0.5 truncate text-2xs leading-tight text-ink-600">
							{[member.role, member.company].filter(Boolean).join(", ") || (
								<span className="text-ink-400">No job title set</span>
							)}
						</p>
						<p className="truncate text-2xs leading-tight text-ink-400">
							{member.email}
						</p>
					</div>
					{onRemoveMember && member.user_id !== lockedId && (
						<RemoveButton
							label={`Remove ${member.name} from this team`}
							onClick={() => onRemoveMember(member)}
						/>
					)}
				</div>
			))}

			{pendingEmails.map((address) => (
				<div
					key={address}
					className="group flex items-center gap-3 rounded-xl border border-amber-200/70 bg-white px-3.5 py-3 shadow-card"
				>
					<span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200">
						<Mail size={15} aria-hidden />
					</span>
					<div className="min-w-0 flex-1">
						<p className="truncate text-[0.875rem] font-medium leading-tight text-ink-900">
							{address}
						</p>
						<p className="mt-0.5 text-2xs leading-tight text-amber-700">
							Invited — no account yet
						</p>
						<p className="truncate text-2xs leading-tight text-ink-400">
							They join the moment they sign up
						</p>
					</div>
					{onRemovePending && (
						<RemoveButton
							label={`Cancel the invitation to ${address}`}
							onClick={() => onRemovePending(address)}
						/>
					)}
				</div>
			))}
		</div>
	)
}

function RemoveButton({ label, onClick }) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-label={label}
			title={label}
			className="shrink-0 rounded-lg p-1.5 text-ink-300 opacity-0 transition hover:bg-ink-100 hover:text-ink-700 focus-visible:opacity-100 group-hover:opacity-100"
		>
			<X size={14} strokeWidth={2.5} aria-hidden />
		</button>
	)
}

/* -------------------------------------------------------------------------- */

function AddTeamDialog({ open, onClose, projectId, attached, onAdded }) {
	const { user, toast } = useApp()
	const [teams, setTeams] = useState([])
	const [loading, setLoading] = useState(true)
	const [busyId, setBusyId] = useState(null)

	const entitled = (user?.plan?.teams ?? 0) !== 0
	const alreadyOn = new Set(attached.map((team) => team.team_id))

	useEffect(() => {
		if (!open || !entitled) return
		setLoading(true)
		api
			.teams()
			.then((data) => setTeams(data.teams))
			.catch(() => setTeams([]))
			.finally(() => setLoading(false))
	}, [open, entitled])

	async function add(team) {
		setBusyId(team.team_id)
		try {
			const result = await api.addProjectTeam(projectId, team.team_id)
			const added = result.added?.length || 0
			toast(
				added
					? `${team.name} added — ${plural(added, "person", "people")} emailed`
					: `${team.name} is on this project`,
				"success"
			)
			await onAdded()
		} catch (cause) {
			toast(cause.message, "error")
		} finally {
			setBusyId(null)
		}
	}

	return (
		<Modal
			open={open}
			onClose={onClose}
			title="Invite a team to this project"
			description="Everyone on it sees every issue here, including the ones raised later."
			footer={<Button onClick={onClose}>Done</Button>}
		>
			{!entitled ? (
				<div className="space-y-3 text-[0.8125rem] leading-relaxed text-ink-600">
					<p>
						Teams are part of the paid plans. Without one you can still invite people
						to an issue at a time.
					</p>
					<Button as={Link} to="/settings/billing?upsell=teams" variant="primary">
						See the plans
					</Button>
				</div>
			) : loading ? (
				<p className="py-6 text-center text-[0.8125rem] text-ink-500">Loading teams…</p>
			) : teams.length === 0 ? (
				<div className="space-y-3 text-[0.8125rem] leading-relaxed text-ink-600">
					<p>You have not created a team yet. A team is a saved group of people.</p>
					<Button as={Link} to="/teams" variant="primary" icon={UsersRound}>
						Create a team
					</Button>
				</div>
			) : (
				<div className="space-y-1.5">
					{teams.map((team) => {
						const on = alreadyOn.has(team.team_id)
						return (
							<div
								key={team.team_id}
								className="flex items-center gap-3 rounded-lg border border-ink-200 px-3 py-2.5"
							>
								<span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-700">
									<UsersRound size={15} aria-hidden />
								</span>
								<div className="min-w-0 flex-1">
									<p className="truncate text-[0.875rem] font-medium text-ink-900">
										{team.name}
									</p>
									<p className="truncate text-2xs text-ink-500">
										{team.members
											.slice(0, 3)
											.map((member) => member.name)
											.join(", ")}
										{team.size > 3 && ` and ${team.size - 3} more`}
									</p>
								</div>
								{on ? (
									<span className="shrink-0 text-2xs font-medium text-ink-400">
										Already on
									</span>
								) : (
									<Button
										size="sm"
										variant="primary"
										onClick={() => add(team)}
										busy={busyId === team.team_id}
										disabled={Boolean(busyId)}
									>
										Add
									</Button>
								)}
							</div>
						)
					})}
				</div>
			)}
		</Modal>
	)
}

function RemoveTeamDialog({ team, projectId, onClose, onRemoved }) {
	const { toast } = useApp()

	return (
		<Modal
			open={Boolean(team)}
			onClose={onClose}
			title={`Remove "${team?.name}" from this project?`}
			size="sm"
			footer={
				<>
					<Button onClick={onClose}>Cancel</Button>
					<Button
						variant="danger"
						icon={UserMinus}
						onClick={async () => {
							const target = team
							onClose()
							try {
								await api.removeProjectTeam(projectId, target.team_id)
								await onRemoved()
								toast(`${target.name} removed from this project`, "info")
							} catch (cause) {
								toast(cause.message, "error")
							}
						}}
					>
						Remove team
					</Button>
				</>
			}
		>
			<p className="text-[0.8125rem] leading-relaxed text-ink-600">
				They stop seeing this project. Anything they were invited to individually
				stays theirs, and everything they have already written stays exactly where it
				is — nothing in the record moves.
			</p>
		</Modal>
	)
}
